import { Component, Input, OnInit } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { combineLatest } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { UtilsService } from '../../../../../core/services/utils.service';

@Component({
  selector: 'app-invoice-gas-doughnut-chart',
  standalone: true,
  imports: [ChartModule],
  templateUrl: './invoice-gas-doughnut-chart.component.html',
  styleUrl: './invoice-gas-doughnut-chart.component.css'
})
export class InvoiceGasDoughnutChartComponent implements OnInit {
  data: any;
  options: any;

  @Input() chartData: number[] = [5.52, 75.63, 16.63];
  @Input() smcNumber: number = 0;

  constructor(private translocoService: TranslocoService, private utilsService: UtilsService) { }

  labels: string[] = [
    'Energy',
    'TMS Charges',
    'Taxes',
    'Unknown'
  ];

  ngOnInit(): void {
    this.updateChart();
    const monthKeys = [
      'shared.energy',
      'shared.tms',
      'invoice.taxes',
      'shared.unknown'
    ];

    combineLatest(monthKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.labels = translations;
      this.updateChart();
    });
  }

  updateChart(): void {
    Chart.register(ChartDataLabels);

    const centerTextPluginGasInvoiceReview = {
      id: 'centerTextPluginGasInvoiceReview',
      beforeDraw(chart: any) {
        const pluginOptions = chart.config.options.plugins?.centerTextPluginGasInvoiceReview;
        if (!pluginOptions || pluginOptions.enabled === false) {
          return;
        }

        const ctx = chart.ctx;

        const { smcNumber = '0' } = pluginOptions;
        const { top, bottom, left, right } = chart.chartArea;
        const centerX = (left + right) / 2;
        const centerY = (top + bottom) / 2;

        const text = `${smcNumber}\n€/smc`;

        ctx.save();
        ctx.font = 'bold 20px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        text.split('\n').forEach((line, index) => {
          ctx.fillText(line, centerX, centerY + index * 20);
        });

        ctx.restore();
      },
    };
    Chart.register(centerTextPluginGasInvoiceReview);

    this.data = {
      labels: this.labels,
      datasets: [
        {
          data: this.chartData,
          backgroundColor: ['#80A7A5', '#1B2827', '#5F8A87', '#C0C0C0'],
          hoverBackgroundColor: ['#80A7A5', '#1B2827', '#5F8A87', '#C0C0C0'],
          borderWidth: 0,
          borderRadius: 6,
          spacing: 2,
        },
      ],
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '65%',
      plugins: {
        legend: {
          position: 'right',
          labels: {
            usePointStyle: true,
            padding: 20,
            color: '#333',
            font: {
              size: 10
            },
            generateLabels: (chart: any) => {
              const data = chart.data;
              const meta = chart.getDatasetMeta(0);

              if (!data.labels?.length) {
                return [];
              }

              return data.labels.map((label: string, index: number) => {
                const wrappedLines = this.utilsService.getMultiLineLabels(label, 20);
                const style = meta.controller.getStyle(index);

                return {
                  text: wrappedLines,
                  fillStyle: style.backgroundColor,
                  strokeStyle: style.borderColor,
                  lineWidth: style.borderWidth,
                  hidden: !chart.getDataVisibility(index),
                  index,
                  pointStyle: 'circle',
                };
              });
            },
          },
        },
        datalabels: {
          display: false,
        },
        tooltip: {
          enabled: true,
          callbacks: {
            label: function (tooltipItem: any) {
              const dataset = tooltipItem.dataset;
              const currentValue = dataset.data[tooltipItem.dataIndex];

              return `${currentValue}`;
            }
          }
        },
        centerTextPluginGasInvoiceReview: {
          enabled: true,
          smcNumber: this.smcNumber
        }
      },
      layout: {
        padding: 40,
      },
    };
  }
}
